import { useLayoutEffect } from "react";
import { useLocation, useSearchParams } from "react-router";
import type {
  GridFilterModel,
  GridInitialState,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { readGridUrlState, updateGridUrlState } from "@/utils/gridUrlState";

type UseGridUrlStateOptions = {
  apiRef: ReturnType<typeof useGridApiRef>;
  defaultSortModel: GridSortModel;
  namespace?: string;
};

type UseGridUrlStateResult = {
  initialState: {
    filter: GridInitialState["filter"];
    sorting?: GridInitialState["sorting"];
  };
  onFilterModelChange: (filterModel: GridFilterModel) => void;
  onSortModelChange: (sortModel: GridSortModel) => void;
};

const serializeFilterModel = (filterModel: GridFilterModel): string =>
  JSON.stringify(filterModel, (key, value: unknown) =>
    key === "fromInput" ? undefined : value
  );

const isDeviceTabResetState = (
  state: unknown
): state is { resetDeviceTab: true } =>
  typeof state === "object" &&
  state !== null &&
  "resetDeviceTab" in state &&
  state.resetDeviceTab === true;

const getValidFilterModel = (
  api: NonNullable<ReturnType<typeof useGridApiRef>["current"]>,
  filterModel: GridFilterModel
): GridFilterModel => ({
  ...filterModel,
  items: filterModel.items.filter((item) => {
    const column = api
      .getAllColumns()
      .find((gridColumn) => gridColumn.field === item.field);

    if (!column || column.filterable === false) return false;

    return (
      column.filterOperators?.some(
        (filterOperator) => filterOperator.value === item.operator
      ) ?? true
    );
  }),
});

const useGridUrlState = ({
  apiRef,
  defaultSortModel,
  namespace,
}: UseGridUrlStateOptions): UseGridUrlStateResult => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const preserveDeviceTabReset = isDeviceTabResetState(location.state);
  const searchString = searchParams.toString();
  const gridUrlState = readGridUrlState(
    searchParams,
    defaultSortModel,
    namespace
  );

  useLayoutEffect(() => {
    const api = apiRef.current;
    if (!api) return;

    const nextGridUrlState = readGridUrlState(
      new URLSearchParams(searchString),
      defaultSortModel,
      namespace
    );
    const nextFilterModel = getValidFilterModel(
      api,
      nextGridUrlState.filterModel
    );

    if (
      serializeFilterModel(api.state.filter.filterModel) !==
      serializeFilterModel(nextFilterModel)
    ) {
      api.setFilterModel(nextFilterModel);
    }

    if (
      nextGridUrlState.sortParam !== "" &&
      JSON.stringify(api.getSortModel()) !==
        JSON.stringify(nextGridUrlState.sortModel)
    ) {
      api.setSortModel(nextGridUrlState.sortModel);
    }
  }, [apiRef, defaultSortModel, namespace, searchString]);

  const updateUrl = (
    filterModel: GridFilterModel,
    sortModel: GridSortModel
  ) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    updateGridUrlState(nextSearchParams, filterModel, sortModel, namespace);
    setSearchParams(nextSearchParams, {
      replace: true,
      preventScrollReset: true,
      state: preserveDeviceTabReset ? { resetDeviceTab: true } : undefined,
    });
  };

  const onFilterModelChange = (filterModel: GridFilterModel) => {
    updateUrl(filterModel, apiRef.current?.getSortModel() ?? defaultSortModel);
  };

  const onSortModelChange = (sortModel: GridSortModel) => {
    updateUrl(
      apiRef.current?.state.filter.filterModel ?? gridUrlState.filterModel,
      sortModel
    );
  };

  return {
    initialState: {
      filter: { filterModel: gridUrlState.filterModel },
      ...(gridUrlState.sortParam !== ""
        ? { sorting: { sortModel: gridUrlState.sortModel } }
        : {}),
    },
    onFilterModelChange,
    onSortModelChange,
  };
};

export { useGridUrlState };
export type { UseGridUrlStateOptions, UseGridUrlStateResult };
